import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { type SerializedError, serializeError as serializeErrorCanonical } from '@/lib/error-serializers';
import { formatCompactNumber, formatLocaleNumber as formatLocaleNumberInternal } from '@/lib/formatters/numbers';
import { buildQueryParams as buildQueryParamsInternal } from './utils/es-toolkit-helpers';

type SerializedErrorWithExtras = SerializedError & { [key: string]: unknown };

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatNumber: typeof formatCompactNumber = (...arguments_) => formatCompactNumber(...arguments_);
export const formatLocaleNumber: typeof formatLocaleNumberInternal = (...arguments_) =>
  formatLocaleNumberInternal(...arguments_);
export const buildQueryParams: typeof buildQueryParamsInternal = (...arguments_) =>
  buildQueryParamsInternal(...arguments_);

export function serializeError(error: unknown, context?: Record<string, unknown>): SerializedErrorWithExtras {
  const serialized = serializeErrorCanonical(error, context);
  if (!context || Object.keys(context).length === 0) {
    return { ...serialized };
  }
  return { ...serialized, ...context };
}

/**
 * Sanitizes text by removing null bytes and control characters that PostgreSQL doesn't accept
 */
export function sanitizeText(text: string | null | undefined): string {
  if (!text) {
    return '';
  }

  let result = '';
  for (const char of text) {
    const codePoint = char.codePointAt(0);
    if (codePoint === undefined) {
      continue;
    }
    if (codePoint === 0) {
      continue;
    }
    if (codePoint < 32 && codePoint !== 9 && codePoint !== 10 && codePoint !== 13) {
      continue;
    }
    if (codePoint === 127) {
      continue;
    }
    result += char;
  }
  return result.trim();
}

/**
 * uniqueStable: Deduplicate an array while preserving the first-seen order.
 *
 * - Uses a Set for O(n) behavior on primitives and referential equality on objects.
 * - For object arrays where you need a stable semantic key, use uniqueStableBy.
 */
export function uniqueStable<T>(array: readonly T[]): T[] {
  const seen = new Set<T>();
  const out: T[] = [];
  for (const item of array) {
    if (!seen.has(item)) {
      seen.add(item);
      out.push(item);
    }
  }
  return out;
}

/**
 * uniqueStableBy: Deduplicate an array by a stable key while preserving order.
 *
 * Example:
 *   uniqueStableBy(users, u => u.id)
 */
export function uniqueStableBy<T, K>(array: readonly T[], keyFunction: (item: T) => K): T[] {
  const seen = new Set<K>();
  const out: T[] = [];
  for (const item of array) {
    const key = keyFunction(item);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(item);
    }
  }
  return out;
}
